import React from 'react';
import type { OrderDeadline } from '../../../dtos/clinical/poc-certification.dto';
import type { OrderCountersign } from '../../../dtos/patientmr/orders.dto';

/**
 * ORD-04 unsigned-order badge: amber once an order has been unsigned for more than 7 days,
 * red after 14. The server's `deadline` wins when present; otherwise the age is counted from
 * the creation date (calendar days).
 */
export type DeadlineLevel = 'ok' | 'amber' | 'red';

export interface UnsignedDeadline {
  level: DeadlineLevel;
  days: number;
}

const DAY_MS = 24 * 60 * 60 * 1000;

function normalizeLevel(level: string | undefined): DeadlineLevel {
  const l = (level || '').toLowerCase();
  if (l === 'red' || l === 'error' || l === 'overdue') return 'red';
  if (l === 'amber' || l === 'yellow' || l === 'orange' || l === 'warning') return 'amber';
  return 'ok';
}

/** Whole calendar days between the creation date and today (local dates). */
export function daysSince(createdAt: string | Date, today: Date = new Date()): number {
  const datePart = createdAt instanceof Date
    ? `${createdAt.getFullYear()}-${String(createdAt.getMonth() + 1).padStart(2, '0')}-${String(createdAt.getDate()).padStart(2, '0')}`
    : createdAt.slice(0, 10);
  const [y, m, d] = datePart.split('-').map(Number);
  if (!y || !m || !d) return 0;
  const start = Date.UTC(y, m - 1, d);
  const end = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.max(0, Math.floor((end - start) / DAY_MS));
}

export function levelForDays(days: number): DeadlineLevel {
  if (days > 14) return 'red';
  if (days > 7) return 'amber';
  return 'ok';
}

export function unsignedDeadline(
  createdAt: string | Date | undefined,
  server?: OrderDeadline | null,
  today: Date = new Date()
): UnsignedDeadline | null {
  if (server && typeof server.days === 'number') {
    return { level: normalizeLevel(server.level), days: server.days };
  }
  if (!createdAt) return null;
  const days = daysSince(createdAt, today);
  return { level: levelForDays(days), days };
}

const BADGE_STYLES: Record<DeadlineLevel, string> = {
  ok: 'bg-slate-100 text-slate-700 border-slate-200',
  amber: 'bg-amber-100 text-amber-800 border-amber-300',
  red: 'bg-red-100 text-red-800 border-red-300',
};

export const UnsignedBadge: React.FC<{ deadline: UnsignedDeadline | null; className?: string }> = ({ deadline, className = '' }) => {
  if (!deadline) return null;
  return (
    <span
      data-testid="unsigned-badge"
      data-level={deadline.level}
      className={`inline-flex items-center rounded border px-2 py-0.5 text-xs font-medium ${BADGE_STYLES[deadline.level]} ${className}`}
    >
      Unsigned · {deadline.days} {deadline.days === 1 ? 'day' : 'days'}
    </span>
  );
};

/**
 * Phase 19 F20: countersignature badge for structured orders. Red once overdue, amber within
 * 3 days of the due date. Legacy orders (no `countersign`) keep the 7/14-day UnsignedBadge.
 */
export function countersignLevel(countersign: OrderCountersign): DeadlineLevel {
  if (countersign.overdue) return 'red';
  if (countersign.daysLeft <= 3) return 'amber';
  return 'ok';
}

const formatDueDate = (value: string): string => {
  const [y, m, d] = value.slice(0, 10).split('-');
  return y && m && d ? `${m}/${d}/${y}` : value;
};

export const CountersignBadge: React.FC<{ countersign: OrderCountersign | null | undefined; className?: string }> = ({ countersign, className = '' }) => {
  if (!countersign) return null;
  const level = countersignLevel(countersign);
  return (
    <span
      data-testid="countersign-badge"
      data-level={level}
      className={`inline-flex items-center rounded border px-2 py-0.5 text-xs font-medium ${BADGE_STYLES[level]} ${className}`}
    >
      Countersign due {formatDueDate(countersign.due)}{countersign.overdue ? ' · overdue' : ''}
    </span>
  );
};
